import { useCallback, useEffect, useState } from 'react'
import { Bike, Clock, Link2, MapPin, Printer, Receipt, ShoppingBag, TrendingUp } from 'lucide-react'
import { CanalPedido, StatusPedido, TipoEntrega, formatarReais } from '@du/shared'
import {
  Badge,
  Button,
  EmptyState,
  Icone,
  Input,
  SectionTitle,
  Skeleton,
  StatTile,
  useAviso,
} from '@du/ui'
import { api, ErroApi } from '../lib/api.js'

type Resumo = {
  dia: string
  pedidos: { total: number; entregues: number; cancelados: number; emAndamento: number }
  faturamentoCents: number
  ticketMedioCents: number
  taxasCents: number
  porCanal: { chave: string; pedidos: number; totalCents: number }[]
  porTipo: { chave: string; pedidos: number; totalCents: number }[]
  porPagamento: { chave: string; pedidos: number; totalCents: number }[]
  porOrigem: { origem: string; nome: string; acessos: number; pedidos: number; conversao: number }[]
  atrasados: number
}

type Pedido = {
  id: string
  numeroDia: number
  status: string
  canal: string
  tipo: string
  motoboy?: { id: string; nome: string } | null
  origem: string | null
  clienteNome: string | null
  bairroNome: string | null
  criadoEm: string
  totalCents?: number
  formaPagamento?: string
  itens: {
    nomeSnapshot: string
    quantidade: number
    observacao: string | null
    adicionais: { nomeSnapshot: string; grupoSnapshot: string }[]
  }[]
}

const TOM: Record<string, 'sucesso' | 'alerta' | 'erro' | 'neutro'> = {
  [StatusPedido.ENTREGUE]: 'sucesso',
  [StatusPedido.CANCELADO]: 'erro',
  [StatusPedido.RECEBIDO]: 'alerta',
}

const hora = (iso: string) =>
  new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

/**
 * O dia operacional de HOJE, do ponto de vista da loja.
 *
 * A loja fecha 01h30. Um pedido da meia-noite e meia pertence ao movimento da
 * véspera — abrir o histórico às 00h30 e ver o dia vazio seria mentira.
 * Antes das 04h, o "hoje" ainda é ontem.
 */
function diaOperacionalHoje(): string {
  const agora = new Date()
  if (agora.getHours() < 4) agora.setDate(agora.getDate() - 1)
  const mes = String(agora.getMonth() + 1).padStart(2, '0')
  const d = String(agora.getDate()).padStart(2, '0')
  return `${agora.getFullYear()}-${mes}-${d}`
}

export function Historico() {
  const { avisar } = useAviso()
  const [motoboys, setMotoboys] = useState<{ id: string; nome: string }[]>([])
  const [dia, setDia] = useState(diaOperacionalHoje())
  const [resumo, setResumo] = useState<Resumo | null>(null)
  const [pedidos, setPedidos] = useState<Pedido[]>([])
  const [copiado, setCopiado] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)

  useEffect(() => {
    void (async () => {
      try {
        setMotoboys(await api<{ id: string; nome: string }[]>('/v1/motoboys'))
      } catch {
        // Sem motoboy cadastrado o seletor some; a tela segue normal.
      }
    })()
  }, [])

  /**
   * Reimprime a comanda de um pedido antigo.
   *
   * Existe aqui, e não só no Kanban, porque o pedido entregue SAI do Kanban
   * — e é justamente o antigo que precisa de segunda via: a comanda que caiu
   * no chão, a que saiu borrada, a conferência do fechamento do caixa.
   *
   * Vai sem `via`: do Histórico não se sabe qual folha faltou, então reimprime
   * o que a impressora estiver configurada para tirar.
   */
  const reimprimir = async (pedido: Pedido) => {
    try {
      await api(`/v1/pedidos/${pedido.id}/reimprimir`, { method: 'POST', corpo: {} })
      avisar({ tom: 'sucesso', texto: `Comanda do #${pedido.numeroDia} reenviada` })
    } catch (e) {
      avisar({
        tom: 'erro',
        texto: 'Falha ao reimprimir',
        ...(e instanceof ErroApi ? { detalhe: e.message } : {}),
      })
    }
  }

  /**
   * Copia o link da conta do cliente.
   *
   * O link vem do SERVIDOR, montado com o código do pedido — a tela não
   * monta URL nenhuma, senão o domínio do cardápio teria de ser configurado
   * num segundo lugar e um dia os dois discordariam.
   */
  const copiarLink = async (pedido: Pedido) => {
    try {
      const { linkAcompanhar } = await api<{ linkAcompanhar?: string }>(
        `/v1/pedidos/${pedido.id}`,
      )
      if (!linkAcompanhar) throw new Error('sem link')
      await navigator.clipboard.writeText(linkAcompanhar)
      setCopiado(pedido.id)
      // Volta ao normal sozinho: um botão que fica "Copiado" para sempre
      // deixa de dizer o que faz.
      setTimeout(() => setCopiado(null), 2500)
    } catch (e) {
      avisar({
        tom: 'erro',
        texto: 'Não consegui copiar o link',
        ...(e instanceof ErroApi ? { detalhe: e.message } : {}),
      })
    }
  }

  /** Troca o motoboy do pedido. Otimista: a tela reage antes da resposta. */
  const atribuir = async (pedido: Pedido, motoboyId: string) => {
    const escolhido = motoboys.find((m) => m.id === motoboyId) ?? null
    setPedidos((atuais) =>
      atuais.map((x) => (x.id === pedido.id ? { ...x, motoboy: escolhido } : x)),
    )
    try {
      await api(`/v1/pedidos/${pedido.id}/motoboy`, {
        method: 'PATCH',
        corpo: { motoboyId: motoboyId || null },
      })
    } catch {
      avisar({ tom: 'erro', texto: `Não consegui atribuir o motoboy do #${pedido.numeroDia}` })
      void carregar()
    }
  }

  const carregar = useCallback(async () => {
    setCarregando(true)
    try {
      const [r, p] = await Promise.all([
        api<Resumo>(`/v1/relatorios/dia?data=${dia}`),
        api<Pedido[]>(`/v1/pedidos?dia=${dia}&limite=200`),
      ])
      setResumo(r)
      setPedidos(p)
    } catch (e) {
      avisar({ tom: 'erro', texto: 'Não consegui carregar', detalhe: e instanceof ErroApi ? e.message : undefined })
    } finally {
      setCarregando(false)
    }
  }, [avisar, dia])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const ehHoje = dia === diaOperacionalHoje()

  return (
    <div className="pagina-gestao">
      <section className="bloco-gestao">
        <SectionTitle
          variante="laranja"
          tamanho="sm"
          acao={
            !ehHoje ? (
              <Button variante="ghost" tamanho="sm" onClick={() => setDia(diaOperacionalHoje())}>
                Voltar para hoje
              </Button>
            ) : null
          }
        >
          {ehHoje ? 'Movimento de hoje' : `Movimento de ${dia.split('-').reverse().join('/')}`}
        </SectionTitle>

        <div className="form-linha">
          <Input
            rotulo="Dia"
            type="date"
            value={dia}
            max={diaOperacionalHoje()}
            onChange={(e) => setDia(e.target.value)}
          />
        </div>

        {carregando ? (
          <Skeleton altura={200} />
        ) : (
          <>
            <div className="grade-stats">
              <StatTile rotulo="Pedidos" valor={String(resumo?.pedidos.total ?? 0)} tom="marca"
                apoio={`${resumo?.pedidos.emAndamento ?? 0} em andamento`} />
              <StatTile rotulo="Faturamento" valor={formatarReais(resumo?.faturamentoCents ?? 0)}
                apoio="só os entregues" />
              <StatTile rotulo="Ticket médio" valor={formatarReais(resumo?.ticketMedioCents ?? 0)} />
              <StatTile rotulo="Taxas de entrega" valor={formatarReais(resumo?.taxasCents ?? 0)} />
            </div>

            {(resumo?.pedidos.cancelados ?? 0) > 0 || (resumo?.atrasados ?? 0) > 0 ? (
              <p className="nota-alerta">
                {resumo?.pedidos.cancelados} cancelado(s)
                {(resumo?.atrasados ?? 0) > 0 ? ` · ${resumo?.atrasados} atrasado(s)` : ''}
              </p>
            ) : null}

            {(resumo?.porCanal.length ?? 0) > 0 ? (
              <div className="grade-stats">
                {resumo?.porCanal.map((c) => (
                  <StatTile
                    key={c.chave}
                    rotulo={c.chave === CanalPedido.WHATSAPP ? 'WhatsApp' : c.chave}
                    valor={String(c.pedidos)}
                    apoio={formatarReais(c.totalCents)}
                  />
                ))}
              </div>
            ) : null}
          </>
        )}
      </section>

      <section className="bloco-gestao">
        <SectionTitle variante="vazado" tamanho="sm">
          Pedidos do dia
        </SectionTitle>

        {carregando ? (
          <Skeleton altura={160} />
        ) : pedidos.length === 0 ? (
          <EmptyState
            icone={Receipt}
            titulo={ehHoje ? 'Nenhum pedido ainda hoje' : 'Nenhum pedido nesse dia'}
            {...(ehHoje ? { texto: 'Assim que entrar o primeiro, ele aparece aqui.' } : {})}
          />
        ) : (
          <ul className="lista-simples">
            {pedidos.map((p) => (
              <li key={p.id}>
                <span>
                  <Icone de={ShoppingBag} tamanho={16} />
                  #{String(p.numeroDia).padStart(3, '0')}
                  <Badge tom={TOM[p.status] ?? 'neutro'}>{p.status}</Badge>
                  {p.canal === CanalPedido.WHATSAPP ? <Badge tom="neutro">WhatsApp</Badge> : null}
                </span>
                <span className="lista-simples__fraco">
                  <Icone de={Clock} tamanho={14} /> {hora(p.criadoEm)}
                  {p.clienteNome ? ` · ${p.clienteNome}` : ''}
                  {p.tipo === TipoEntrega.ENTREGA ? (
                    <> · <Icone de={MapPin} tamanho={14} /> {p.bairroNome ?? 'entrega'}</>
                  ) : ' · retirada'}
                  {p.totalCents !== undefined ? ` · ${formatarReais(p.totalCents)}` : ''}
                  {p.formaPagamento ? ` · ${p.formaPagamento}` : ''}
                </span>
                {/*
                  Sempre editável, em qualquer status — inclusive depois de
                  ENTREGUE. Se o atendente esquecer de atribuir na correria, o
                  pedido sai do Kanban e, sem isto, não haveria onde corrigir:
                  o ganho ficaria errado para sempre e a entrega apareceria
                  como "sem motoboy" sem recurso.
                */}
                {p.tipo === TipoEntrega.ENTREGA && motoboys.length > 0 ? (
                  <label className="kanban__motoboy">
                    <Icone de={Bike} tamanho={16} />
                    <span className="kanban__motoboy__rotulo">Motoboy</span>
                    <select
                      value={p.motoboy?.id ?? ''}
                      onChange={(e) => void atribuir(p, e.target.value)}
                      aria-label={`Motoboy do pedido ${p.numeroDia}`}
                    >
                      <option value="">a definir</option>
                      {motoboys.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.nome}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <span className="lista-simples__acoes">
                  <Button
                    variante="ghost"
                    tamanho="sm"
                    icone={Printer}
                    onClick={() => void reimprimir(p)}
                    aria-label={`Reimprimir comanda do pedido ${p.numeroDia}`}
                  >
                    Reimprimir
                  </Button>
                  {/*
                    O link da conta do cliente, para o atendente reenviar à
                    mão pela conversa que já existe. É a saída para "perdi o
                    link": antes disso, o cliente ligava e não havia nada para
                    dar a ele.
                  */}
                  <Button
                    variante="ghost"
                    tamanho="sm"
                    icone={Link2}
                    onClick={() => void copiarLink(p)}
                    aria-label={`Copiar link de acompanhamento do pedido ${p.numeroDia}`}
                  >
                    {copiado === p.id ? 'Copiado' : 'Copiar link'}
                  </Button>
                </span>
                <span className="lista-simples__fraco conversa__previa">
                  {p.itens
                    .map((i) => {
                      const extras = i.adicionais.map((a) => a.nomeSnapshot)
                      if (i.observacao) extras.push(i.observacao)
                      return `${i.quantidade}× ${i.nomeSnapshot}${extras.length > 0 ? ` (${extras.join(', ')})` : ''}`
                    })
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {(resumo?.porOrigem.length ?? 0) > 0 ? (
        <section className="bloco-gestao">
          <SectionTitle variante="vazado" tamanho="sm">De onde vieram</SectionTitle>
          <ul className="lista-simples">
            {resumo?.porOrigem.map((o) => (
              <li key={o.origem}>
                <span>
                  <Icone de={TrendingUp} tamanho={16} /> {o.nome}
                </span>
                <span className="lista-simples__fraco">
                  {o.acessos} acesso(s) · {o.pedidos} pedido(s) · {o.conversao}% viraram pedido
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}
